import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  GpuFormFieldAppearance,
  GpuFormFieldModule,
} from '@geneplanet/ngx-ui/src/lib/form-field';
import {
  ControlContainer,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { HabitsQuestion } from 'src/app/analyses/interfaces/analyses.types';
import { controlContainerProvider } from '../../constants/control-container-provider';
import { GpuRadioGroupModule } from '@geneplanet/ngx-ui/src/lib/radio-group';
import { AnswerDict } from '../../interfaces/answer-dict.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AnswerDto } from '../../interfaces/answer-dto.interface';
import { AnalyticsService } from '@geneplanet/ngx-utils/src/lib/analytics';

@UntilDestroy()
@Component({
  selector: 'app-input-options',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    GpuRadioGroupModule,
    GpuFormFieldModule,
  ],
  templateUrl: './input-options.component.html',
  styleUrls: ['./input-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [controlContainerProvider],
})
export class InputOptionsComponent implements OnDestroy {
  public questionnaireId: string;
  public question: HabitsQuestion;
  public formFieldAppearance: GpuFormFieldAppearance;
  public answer: AnswerDto;

  private parentContainer = inject(ControlContainer);
  private analyticsService = inject(AnalyticsService);

  get form() {
    return this.parentContainer.control as FormGroup;
  }

  public init(): void {
    this.form.addControl(
      'select',
      new FormControl(this.answer?.id ?? '', Validators.required)
    );
    this.form.addControl('input', new FormControl(this.answer?.value ?? ''));

    if (this.answer?.id) {
      this.handleValidators(this.answer?.id);
    }

    this.form
      .get('select')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        this.handleValidators(value);
      });
  }

  public onSubmit(): AnswerDict {
    const answer = { id: this.form.controls.select.value };
    if (this.selectedInput(this.form.controls.select.value)) {
      answer['value'] = this.form.controls.input.value;
    }

    return {
      [this.question.id]: [answer],
    };
  }

  ngOnDestroy(): void {
    this.form.removeControl('select');
    this.form.removeControl('input');
  }

  protected split(name: string, index: number): string {
    return name.split('<b>x</b>')[index];
  }

  protected track(isInput = false): void {
    const data = {
      id: this.form.controls.select.value,
    };
    if (isInput) {
      data['value'] = this.form.controls.input.value;
    }

    this.analyticsService.trackFormValue(
      'analyses-add',
      'value',
      data,
      this.questionnaireId,
      this.question.id
    );
  }

  private handleValidators(value: string) {
    const input = this.form.get('input');

    if (value && this.selectedInput(value)) {
      const inputAnswer = this.question.answers.find(
        (answer) => answer.type === 'Input'
      );

      const { min, max, decimalPlaces } = inputAnswer;
      const pattern = new RegExp(
        `^\\d+${decimalPlaces > 0 ? `\\.?\\d{0,${decimalPlaces}}` : ''}$`
      );

      input.addValidators([
        Validators.required,
        Validators.min(min),
        Validators.max(max),
        Validators.pattern(pattern),
      ]);
    } else {
      input.clearValidators();
    }
    input.updateValueAndValidity();
  }

  private selectedInput(value: string): boolean {
    return this.question.answers.find((a) => a.id === value).type === 'Input';
  }
}
